import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { postContactUs } from '../../../api/apiServices';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';

const DOUBLE_ARROW_ICON =
  'https://assets.askhippo.co.za/Icons/common/DoubleArrow_White.svg';

const ENQUIRY_TYPES = [
  'Website Related',
  'Service Related',
  'Advertising',
  'Online Advertising',
  'Insurance Partner',
  'Legal Compliance',
  'Complaint',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.875rem',
    color: '#162F3B',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#A2ACB1' },
    '&:hover fieldset': { borderColor: '#162F3B' },
    '&.Mui-focused fieldset': { borderColor: '#162F3B', borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.875rem',
    color: '#738289',
    '&.Mui-focused': { color: '#162F3B' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.75rem',
  },
};

const initialForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  regarding: '',
  message: '',
};

function ContactFormComponent() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, phone: digits }));
  };

  function validate() {
    const e = {};
    const cellRegex = /^0\d{9}$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!cellRegex.test(form.phone.trim()))
      e.phone = 'Please enter a valid South African cellphone number';
    if (!emailRegex.test(form.email.trim()))
      e.email = 'Please enter a valid email address';
    if (!form.regarding) e.regarding = 'Please select a type of enquiry';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setLoading(true);
    setApiError('');
    try {
      await postContactUs(form);
      setSubmitted(true);
      setForm(initialForm);
    } catch {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '40px',
      }}
    >
      {/* Header */}
      <ScrollFadeWrapper variant="text">
        <Box sx={{ textAlign: 'center', maxWidth: '908px' }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '12px',
            }}
          >
            How can we help?
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
            }}
          >
            Our team is here to assist. Just enter your details below and
            we&apos;ll point you in the right direction.
          </Typography>
        </Box>
      </ScrollFadeWrapper>

      {/* Form Card */}
      <ScrollFadeWrapper>
        <Box
          sx={{
            width: '912px',
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            px: '40px',
            py: '32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {!submitted && (
            <ScrollFadeWrapper variant="text">
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1.5rem',
                  lineHeight: '1.75rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                }}
              >
                Get in touch
              </Typography>
            </ScrollFadeWrapper>
          )}

          <Box sx={{ overflow: 'visible' }}>
            <Collapse in={submitted} timeout={400} unmountOnExit>
              <Box sx={{ py: '24px', textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#162F3B',
                  }}
                >
                  Thank you! We&apos;ll be in touch shortly.
                </Typography>
              </Box>
            </Collapse>

            <Collapse in={!submitted} timeout={400} unmountOnExit>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
              >
                {/* Name row */}
                <ScrollFadeWrapper>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '16px',
                    }}
                  >
                    <TextField
                      label="First name"
                      value={form.firstName}
                      onChange={set('firstName')}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      label="Last name"
                      value={form.lastName}
                      onChange={set('lastName')}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>
                </ScrollFadeWrapper>

                {/* Contact row */}
                <ScrollFadeWrapper>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '16px',
                    }}
                  >
                    <TextField
                      label="Phone number"
                      value={form.phone}
                      onChange={setPhone}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      label="Email address"
                      value={form.email}
                      onChange={set('email')}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>
                </ScrollFadeWrapper>

                {/* Type of enquiry */}
                <ScrollFadeWrapper>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.regarding)}
                    sx={fieldSx}
                  >
                    <Select
                      value={form.regarding}
                      onChange={set('regarding')}
                      displayEmpty
                      sx={{
                        height: '53px',
                        borderRadius: '12px',
                        backgroundColor: '#FFFFFF',
                        '& .MuiSelect-select': {
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '1rem',
                          lineHeight: '1.2rem',
                          letterSpacing: '-0.02em',
                          color: form.regarding ? '#162F3B' : '#738289',
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiSelect-icon': { color: '#162F3B' },
                      }}
                      renderValue={(selected) =>
                        selected ? (
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '1rem',
                              lineHeight: '1.2rem',
                              color: '#162F3B',
                            }}
                          >
                            {selected}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '1rem',
                              lineHeight: '1.2rem',
                              color: '#738289',
                            }}
                          >
                            Type of enquiry
                          </Typography>
                        )
                      }
                      MenuProps={{
                        disablePortal: false,
                        slotProps: {
                          paper: {
                            sx: {
                              mt: '10px',
                              maxHeight: 'calc(53px * 6)',
                              overflowY: 'auto',
                              border: '1px solid #A2ACB1',
                              borderRadius: '12px',
                              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
                              '&::-webkit-scrollbar': { width: 0, height: 0 },
                              msOverflowStyle: 'none',
                              scrollbarWidth: 'none',
                            },
                          },
                        },
                        MenuListProps: {
                          sx: {
                            py: 0,
                            '&::-webkit-scrollbar': { width: 0, height: 0 },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                            maxHeight: 'calc(53px * 6)',
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      {ENQUIRY_TYPES.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '1rem',
                            color: '#162F3B',
                            height: '53px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            px: '28px',
                            '&.Mui-selected': {
                              backgroundColor: 'transparent !important',
                            },
                          }}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.regarding && (
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          color: '#D32F2F',
                          mt: '3px',
                          ml: '14px',
                        }}
                      >
                        {errors.regarding}
                      </Typography>
                    )}
                  </FormControl>
                </ScrollFadeWrapper>

                {/* Message */}
                <ScrollFadeWrapper>
                  <TextField
                    label="Your message"
                    value={form.message}
                    onChange={set('message')}
                    error={Boolean(errors.message)}
                    helperText={errors.message}
                    multiline
                    rows={5}
                    fullWidth
                    sx={fieldSx}
                  />
                </ScrollFadeWrapper>

                {apiError && (
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.875rem',
                      color: '#D32F2F',
                    }}
                  >
                    {apiError}
                  </Typography>
                )}

                {/* Submit */}
                <ScrollFadeWrapper>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: '8px',
                    }}
                  >
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleSubmit}
                      disabled={loading}
                      sx={{
                        minHeight: '48px',
                        px: '32px',
                        borderRadius: '999px',
                        backgroundColor: '#FF007A',
                        color: '#FFFFFF',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        lineHeight: 1,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#E6006D' },
                        '&:disabled': {
                          backgroundColor: '#FFAAD3',
                          color: '#FFFFFF',
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                      ) : (
                        <>
                          Submit
                          <img
                            src={DOUBLE_ARROW_ICON}
                            alt=""
                            width={16}
                            height={16}
                            style={{ marginLeft: '8px', display: 'block' }}
                          />
                        </>
                      )}
                    </Button>
                  </Box>
                </ScrollFadeWrapper>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </ScrollFadeWrapper>
    </Box>
  );
}

export default ContactFormComponent;
